import {
    Callout,
    IconWarningRegular,
    Text2,
    Text6,
    IconChevronLeftRegular,
    Text4,
    NavigationBar,
    ThemeVariant,
    skinVars,
    EmptyState,
    IconBoxRegular,
    Spinner,
} from '@telefonica/mistica';
import './index.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { Order } from '../../../../models/Orders';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { getOrderDetail } from '../../../../services/OrderService';
import { TrackingEvents } from '../../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../../utils/navigation/controlDevices';
import { OrderStatusTag } from '../statusTags';
import { AppointmentInfoSection } from '../../../../components/AppointmentInfoSecction';
import { TrackingInfoSection } from '../../../../components/TrackingInfoSection';
import { PortabilityInfoSection } from '../../../../components/PortabilityInfoSection';
import { NewLinesInfoSection } from '../../../../components/NewLinesSection';
import BreadCrumbsWrapper from '../../../../components/BreadcrumbsWrapper';

export const OrderDetail: React.FC = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(true);
    const prevScrollY = useRef(0);
    const navigate = useNavigate();
    const { trackingScreenView } = new TrackingEvents();
    const dates = useRef<HTMLDivElement>(null);
    const [params] = useSearchParams();
    const [order, setOrder] = useState<Order>();
    const [loading, setLoading] = useState(true);

    let throttleTimer: any;
    const throttle = (callback: any, time: any) => {
        if (throttleTimer) return;
        throttleTimer = true;
        setTimeout(() => {
            callback();
            throttleTimer = false;
        }, time);
    };

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
        getOrderDetail(params.get('id')!).then((response: AxiosResponse) => {
            setOrder(response.data);
            setLoading(false);
        });
        const handleScroll = () => {
            const heightCard = dates.current?.scrollHeight! - dates.current?.clientHeight!;
            const currentScrollY = window.scrollY;
            prevScrollY.current = currentScrollY;
            setVisible(heightCard + 1 > currentScrollY);
        };
        window.addEventListener('scroll', () => {
            throttle(handleScroll, 250);
        });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const getTittleEmpty = (status: string) => {
        switch (status) {
            case 'IN_PROGRESS':
                return t('orderProgressEmpty');
            case 'CANCELED':
                return t('orderCancel');
            case 'FINISHED':
                return t('orderReadyEmpty');
            default:
                return '';
        }
    };

    return (
        <div className='orderDetail-page'>
            {loading ? (
                <div className='c-spinner'>
                    <Spinner size={24} />
                    <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
                </div>
            ) : (
                order && (
                    <>
                        <div className='orderDetail-page__header'>
                            {!(isWebviewAndroid || isWebviewIOS) && (
                                <>
                                    <div className='orderDetail-page__header-navigationBar'>
                                        <NavigationBar
                                            onBack={() => {
                                                navigate(-1);
                                            }}
                                            title={t('orderDetail')}
                                        />
                                    </div>
                                    <div className='orderDetail-page__header-return'>
                                        <div
                                            className='orderDetail-page__header-return-cursor'
                                            onClick={() => {
                                                navigate(-1);
                                            }}
                                        >
                                            <div className='orderDetail-page__header-return-cursor-icon'>
                                                <IconChevronLeftRegular size={20} />
                                            </div>
                                            <Text2 medium>{t('returnResponsive')}</Text2>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className='orderDetail-page__header-breadcrumbs'>
                                <ThemeVariant>
                                    <BreadCrumbsWrapper
                                        title={t('orderDetail')}
                                        breadcrumbs={[{ title: t('profileOrders'), url: '/profile/orders' }]}
                                    />
                                </ThemeVariant>
                            </div>
                            <div className='orderDetail-page__header-scroll' ref={dates}>
                                <div className='orderDetail-page__header-scroll-tag'>
                                    {<OrderStatusTag status={order?.status}></OrderStatusTag>}
                                </div>
                                {visible ? (
                                    <div className='orderDetail-page__header-scroll-product'>
                                        <Text4 regular>{order?.description}</Text4>
                                    </div>
                                ) : (
                                    <div className='orderDetail-page__header-scroll-product'>
                                        <Text6>{order?.description}</Text6>
                                    </div>
                                )}
                            </div>
                            <div className={`${visible ? 'orderDetail-page__header-state' : 'hidden'}`}>
                                <div className='orderDetail-page__header-state-flex'>
                                    <div className='orderDetail-page__header-state-flex-literal'>
                                        <Text2 regular>{t('ordersState')}</Text2>
                                    </div>
                                    <div className='orderDetail-page__header-state-flex-date'>
                                        <Text2 regular>{dayjs(order?.startDate as any).format('DD/MM/YYYY')}</Text2>
                                    </div>
                                </div>
                                {order?.finishDate && (
                                    <div className='orderDetail-page__header-state-flex'>
                                        <div className='orderDetail-page__header-state-flex-literal'>
                                            <Text2 regular>{t('ordersTagDone')}</Text2>
                                        </div>
                                        <div className='orderDetail-page__header-state-flex-date'>
                                            <Text2 regular>
                                                {dayjs(order?.finishDate as any).format('DD/MM/YYYY')}
                                            </Text2>
                                        </div>
                                    </div>
                                )}
                                {order.price && (
                                    <div className='orderDetail-page__header-state-price'>
                                        <div className='orderDetail-page__header-state-price-title'>
                                            <Text2 regular>{t('devicePrice')}</Text2>
                                        </div>
                                        <div className='orderDetail-page__header-state-price-number'>
                                            <Text2 regular>{order.price}€</Text2>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='orderDetail-page__content'>
                            {order?.status === 'INCIDENCE' &&
                                (order?.incidenceTexts?.length > 0 ? (
                                    order?.incidenceTexts?.map((text, index) => {
                                        return (
                                            <div className='orderDetail-page__content-callout' key={index}>
                                                <Callout
                                                    icon={
                                                        <IconWarningRegular color={skinVars.colors.error} size={24} />
                                                    }
                                                    title={t('ordersTagIncidence')}
                                                    description={text}
                                                ></Callout>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className='orderDetail-page__content-callout'>
                                        <Callout
                                            icon={<IconWarningRegular color={skinVars.colors.error} size={24} />}
                                            title={t('ordersTagIncidence')}
                                            description={t('orderDetailsIncidentTextGeneric')}
                                        ></Callout>
                                    </div>
                                ))}
                            {!order.appointmentInfo &&
                                !order.trackingInfo &&
                                !order.portabilityInfos &&
                                !order.newLineNumbers &&
                                order?.status !== 'INCIDENCE' && (
                                    <div className='orderDetail-page__content-emptyState'>
                                        <EmptyState
                                            icon={<IconBoxRegular color={skinVars.colors.brand} size={64} />}
                                            title={getTittleEmpty(order?.status)}
                                        ></EmptyState>
                                    </div>
                                )}
                            {order?.appointmentInfo && (
                                <div className='orderDetail-page__content-appoinmentInfo'>
                                    <AppointmentInfoSection
                                        orderId={order.id}
                                        appointmentInfo={order.appointmentInfo}
                                        orderDescription={order.description}
                                        scheduledFiberTransferDate={order.scheduledFiberTransferDate}
                                    ></AppointmentInfoSection>
                                </div>
                            )}
                            {order?.portabilityInfos && (
                                <div className='orderDetail-page__content-portabilityInfos'>
                                    <PortabilityInfoSection
                                        portabilityInfos={order.portabilityInfos}
                                    ></PortabilityInfoSection>
                                </div>
                            )}
                            {order?.newLineNumbers && (
                                <div className='orderDetail-page__content-newLineNumbers'>
                                    <NewLinesInfoSection newLineNumbers={order.newLineNumbers}></NewLinesInfoSection>
                                </div>
                            )}
                            {order?.trackingInfo && (
                                <div className='orderDetail-page__content-trackingInfo'>
                                    <TrackingInfoSection
                                        trackingInfo={order.trackingInfo}
                                        orderId={order.id}
                                    ></TrackingInfoSection>
                                </div>
                            )}
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default OrderDetail;
